import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  ListMusic,
  Heart,
  Speaker,
  Plus,
  ListPlus,
  Check,
  X,
  Shuffle,
  ListOrdered,
  Sparkles,
  Repeat,
  Repeat1,
  ListEnd,
} from 'lucide-react';
import SeekIcon from './SeekIcon';
import { useAudioOutput } from '../hooks/useAudioOutput';
import { useUserPlaylists } from '../hooks/useUserPlaylists';
import { toasts } from '../utils/toasts';
// Sentry lazy-loaded on first error to keep initial bundle lean
const captureException = (err, opts) => {
  if (!import.meta.env.PROD) {
    console.error('[Player]', err);
    return;
  }
  import('@sentry/react')
    .then((Sentry) => {
      Sentry?.captureException?.(err, opts);
    })
    .catch(() => {});
};
import { toast } from 'sonner';
import { useIOSAudioUnlock } from '../hooks/useIOSAudioUnlock';
import { usePlayer } from '../contexts/PlayerContext';
import { usePlayerStore } from '../stores/playerStore';
import PlaylistCover from './PlaylistCover';
import { useSettingsContext } from '../contexts/SettingsContext';
import { useAudioCrossfade } from '../hooks/useAudioCrossfade';
import { tracks as allTracks } from '../data/tracks';
import {
  getSmartNextTrack,
  getCurrentTimeProfile,
  smartShuffle2Opt,
} from '../data/playlistEngine';
import { capture } from '../lib/posthog';
import { usePostHogEvents } from '../hooks/usePostHogEvents';

function Player({
  currentTrack,
  isPlaying,
  setIsPlaying,
  playlist,
  setCurrentTrackIndex,
  currentTrackIndex,
  onOpenFullscreen,
  onTimeUpdate,
  volumeExternal,
  onVolumeChange,
  onAudioRef,
  onOpenQueue,
  isQueueOpen,
}) {
  // Dual audio refs for crossfade
  const audioRefA = useRef(null);
  // eslint-disable-next-line no-unused-vars -- audioRefB used below for crossfade + useAudioOutput
  const audioRefB = useRef(null);

  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const volume = volumeExternal !== undefined ? volumeExternal : 0.7;
  const setVolume = onVolumeChange || (() => {});
  const [repeat, setRepeat] = useState('off'); // 'off' | 'all' | 'one'
  const [playMode, setPlayMode] = useState('sequential'); // 'sequential' | 'shuffle' | 'smart'

  const PLAY_MODES = {
    sequential: { Icon: ListOrdered, label: "Lecture dans l'ordre" },
    shuffle: { Icon: Shuffle, label: 'Lecture aléatoire' },
    smart: { Icon: Sparkles, label: 'Lecture intelligente' },
  };
  const REPEAT_MODES = {
    off: { Icon: Repeat, label: 'Répétition désactivée' },
    all: { Icon: Repeat, label: 'Répéter la playlist' },
    one: { Icon: Repeat1, label: 'Répéter cette piste' },
  };

  const cyclePlayMode = () => {
    const order = ['sequential', 'shuffle', 'smart'];
    const next = order[(order.indexOf(playMode) + 1) % order.length];
    setPlayMode(next);
    capture('player_mode_changed', { mode: next });
  };

  const cycleRepeatMode = () => {
    const order = ['off', 'all', 'one'];
    const next = order[(order.indexOf(repeat) + 1) % order.length];
    setRepeat(next);
    capture('player_repeat_changed', { mode: next });
  };
  const ActiveModeIcon = PLAY_MODES[playMode].Icon;
  const ActiveRepeatIcon = REPEAT_MODES[repeat].Icon;

  useIOSAudioUnlock(audioRefA);
  const {
    isLiked,
    toggleLike,
    playTrack,
    history,
    addToQueue,
    queue,
    setQueue,
    setPlaylist,
  } = usePlayer();
  const { settings } = useSettingsContext();
  const events = usePostHogEvents();
  const userPlaylists = useUserPlaylists();
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const addMenuRef = useRef(null);
  const newNameInputRef = useRef(null);

  // Phase 3 Step 5 — AirPlay 2 / audio output picker
  const {
    supported: audioOutputSupported,
    picking: audioOutputPicking,
    pickOutput,
  } = useAudioOutput([audioRefA, audioRefB]);

  const crossfadeDuration = settings?.crossfadeDuration ?? 3;
  // eslint-disable-next-line no-unused-vars -- startCrossfadeTo reserved for future end-of-track crossfade
  const { activeIndex, startCrossfadeTo } = useAudioCrossfade(
    audioRefA,
    audioRefB,
    {
      duration: crossfadeDuration,
    }
  );

  // Expose the active audio ref to parent (for seek + time tracking)
  const activeAudioRef = activeIndex === 0 ? audioRefA : audioRefB;

  useEffect(() => {
    if (onAudioRef) onAudioRef(activeAudioRef);
  }, [onAudioRef, activeIndex]); // eslint-disable-line -- react-hooks/exhaustive-deps: activeAudioRef changes with activeIndex

  const seekRelative = useCallback(
    (delta) => {
      const audio = activeAudioRef.current;
      if (!audio) return;
      const dur = audio.duration;
      const next = audio.currentTime + delta;
      audio.currentTime = Number.isFinite(dur)
        ? Math.max(0, Math.min(dur, next))
        : Math.max(0, next);
      capture('player_seek_relative', { delta });
    },
    [activeAudioRef]
  );

  // Close add-to-playlist menu on click outside
  useEffect(() => {
    if (!showAddMenu) return;
    const handler = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
        setCreatingNew(false);
        setNewPlaylistName('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAddMenu]);

  const startCreatePlaylist = () => {
    setCreatingNew(true);
    setNewPlaylistName('');
    setTimeout(() => newNameInputRef.current?.focus(), 30);
  };
  const cancelCreatePlaylist = () => {
    setCreatingNew(false);
    setNewPlaylistName('');
  };
  const submitCreatePlaylist = () => {
    const name = newPlaylistName.trim();
    if (!name || !currentTrack) return;
    const newPl = userPlaylists.create(name);
    if (newPl?.id) {
      userPlaylists.addTrack(newPl.id, currentTrack.id);
      toasts.success?.(`Ajoutée à "${name}"`);
      setShowAddMenu(false);
      setCreatingNew(false);
      setNewPlaylistName('');
      capture('track_added_to_playlist', {
        trackId: currentTrack.id,
        playlistId: newPl.id,
        viaCreate: true,
        source: 'player',
      });
    }
  };
  const addCurrentToPlaylist = (targetId, targetName) => {
    if (!currentTrack) return;
    userPlaylists.addTrack(targetId, currentTrack.id);
    toasts.success?.(`Ajoutée à "${targetName}"`);
    setShowAddMenu(false);
    capture('track_added_to_playlist', {
      trackId: currentTrack.id,
      playlistId: targetId,
      source: 'player',
    });
  };

  // Auto-continuation
  //   1. Manual queue ("À suivre") consumed first — Spotify-style
  //   2. playMode dictates behavior:
  //      - sequential : advance index (wrap if repeat=all, else stop)
  //      - shuffle    : random within playlist (no leak to allTracks)
  //      - smart      : sequential through playlist, then radio-extend at end via algo
  const playNext = useCallback(
    (source = 'manual') => {
      // PostHog: track_skipped if user manually skips before 80% of track
      if (source === 'manual' && currentTrack) {
        const audio = activeAudioRef.current;
        const secondsListened = audio?.currentTime || 0;
        const totalDuration = audio?.duration || 0;
        if (totalDuration > 0 && secondsListened < totalDuration * 0.8) {
          capture('track_skipped', {
            trackId: currentTrack.id,
            title: currentTrack.title,
            secondsListened,
            source: 'manual',
          });
        }
      }

      const playlistLen = playlist?.length || 0;
      const idx =
        typeof currentTrackIndex === 'number' ? currentTrackIndex : -1;

      // 1. Manual queue priority — insert head into playlist after current, advance.
      if (queue && queue.length > 0) {
        const head = queue[0];
        setQueue(queue.slice(1));
        const insertAt = idx + 1;
        const newPlaylist = [
          ...playlist.slice(0, insertAt),
          head,
          ...playlist.slice(insertAt),
        ];
        setPlaylist(newPlaylist);
        setCurrentTrackIndex(insertAt);
        return;
      }

      // 2. Shuffle: random within playlist, never leak outside.
      if (playMode === 'shuffle' && playlistLen > 1) {
        let next = Math.floor(Math.random() * playlistLen);
        if (next === idx) next = (next + 1) % playlistLen;
        setCurrentTrackIndex(next);
        return;
      }

      // 3. Sequential / smart while playlist has remaining tracks.
      if (playlistLen > 0 && idx < playlistLen - 1) {
        setCurrentTrackIndex(idx + 1);
        return;
      }

      // 4. End of playlist reached.
      // Smart mode: extend with algorithmic radio (append, never collapse).
      if (playMode === 'smart') {
        const businessType = settings?.businessType || 'restaurant';
        const timeOfDay = getCurrentTimeProfile();
        const excludeIds = [
          ...(currentTrack ? [currentTrack.id] : []),
          ...playlist.map((t) => t.id),
        ];
        const smart = getSmartNextTrack(currentTrack, history, allTracks, {
          excludeIds,
          businessType,
          timeOfDay,
        });
        if (smart) {
          const newPlaylist = [...playlist, smart];
          setPlaylist(newPlaylist);
          setCurrentTrackIndex(newPlaylist.length - 1);
        }
        return;
      }

      // Sequential / shuffle at end: wrap if repeat=all, else stop.
      if (repeat === 'all' && playlistLen > 0) {
        setCurrentTrackIndex(0);
        return;
      }
      // No more tracks, no repeat — stop.
      setIsPlaying(false);
    },
    [
      queue,
      setQueue,
      playlist,
      setPlaylist,
      playMode,
      repeat,
      currentTrackIndex,
      currentTrack,
      history,
      setCurrentTrackIndex,
      setIsPlaying,
      settings,
      activeAudioRef,
    ]
  );

  // Handle track-end: respects repeat mode (one = replay current, else = playNext)
  const handleTrackEnd = useCallback(() => {
    const audio = activeAudioRef.current;
    if (repeat === 'one' && audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {});
      return;
    }
    playNext('end');
  }, [repeat, activeAudioRef, playNext]);

  const playPrev = useCallback(() => {
    const activeAudio = activeAudioRef.current;
    if (activeAudio && activeAudio.currentTime > 3) {
      activeAudio.currentTime = 0;
    } else if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
    }
  }, [currentTrackIndex, setCurrentTrackIndex, activeAudioRef]);

  const handleAudioError = (err) => {
    console.error('[Player] audio error:', err);
    captureException(err, {
      tags: { component: 'Player', track_id: currentTrack?.id ?? 'unknown' },
      extra: { src: activeAudioRef.current?.src },
    });
    toast.error(
      'Erreur audio' + (currentTrack?.title ? ': ' + currentTrack.title : '')
    );
  };

  const loadedTrackIdRef = useRef(null);
  const positionRestoredRef = useRef(false);

  // CRITICAL : prevent ANY autoplay until user explicitly initiates playback this session.
  // Defends against : (1) refresh + persisted currentTrack, (2) MediaSession play action
  // dispatched by OS/browser on tab restore, (3) StrictMode double-effect stale closures,
  // (4) async race in getOfflineUrl().then() callback.
  const userInitiatedPlayRef = useRef(false);
  const previousIsPlayingRef = useRef(isPlaying);
  useEffect(() => {
    // Mark as user-initiated only on a false→true transition AFTER initial mount.
    // The initial mount has previousIsPlayingRef.current === isPlaying (no transition).
    if (!previousIsPlayingRef.current && isPlaying) {
      userInitiatedPlayRef.current = true;
    }
    previousIsPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // On mount: pause both audio elements + force isPlaying=false (safety net for stale state)
  // PLUS: monkey-patch audio.play() to no-op until user explicitly clicks play.
  // This is the nuclear option to defeat ANY auto-resume mechanism (browser tab restore,
  // MediaSession session restoration, OS-level resume, hot-reload race conditions, etc).
  useEffect(() => {
    const audioA = audioRefA.current;
    const audioB = audioRefB.current;
    if (!audioA || !audioB) return;

    audioA.pause();
    audioB.pause();
    audioA.currentTime = 0;
    audioB.currentTime = 0;
    if (isPlaying) setIsPlaying(false);

    // Monkey-patch play() : block any call until userInitiatedPlayRef.current is true.
    const originalPlayA = audioA.play.bind(audioA);
    const originalPlayB = audioB.play.bind(audioB);
    audioA.play = function () {
      if (!userInitiatedPlayRef.current) {
        return Promise.resolve();
      }
      return originalPlayA();
    };
    audioB.play = function () {
      if (!userInitiatedPlayRef.current) {
        return Promise.resolve();
      }
      return originalPlayB();
    };

    return () => {
      // Restore on unmount (defensive, mostly never runs in practice since Player is global)
      try {
        audioA.play = HTMLMediaElement.prototype.play;
      } catch {
        /* readonly */
      }
      try {
        audioB.play = HTMLMediaElement.prototype.play;
      } catch {
        /* readonly */
      }
    };
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: mount only, refs stable
  }, []);

  useEffect(() => {
    const audio = activeAudioRef.current;
    if (!audio) return;

    // Only re-set src if track changed
    if (currentTrack?.id !== loadedTrackIdRef.current) {
      loadedTrackIdRef.current = currentTrack?.id ?? null;
      positionRestoredRef.current = false;

      if (currentTrack) {
        audio.src = currentTrack.file ?? '';
        // Restore persisted playback position ONCE per track load (after refresh)
        const onMetadata = () => {
          if (!positionRestoredRef.current) {
            const persistedTime = usePlayerStore.getState().currentTime;
            if (
              persistedTime > 0 &&
              audio.duration &&
              persistedTime < audio.duration
            ) {
              audio.currentTime = persistedTime;
            }
            positionRestoredRef.current = true;
          }
          audio.removeEventListener('loadedmetadata', onMetadata);
        };
        audio.addEventListener('loadedmetadata', onMetadata);
        // GUARD: never autoplay on initial track load after refresh.
        if (isPlaying && userInitiatedPlayRef.current) {
          audio.play().catch(handleAudioError);
        } else {
          audio.pause();
        }
        // PostHog: track_played event on track change (only when user actually started)
        if (userInitiatedPlayRef.current) {
          events.trackPlayed({ track_id: currentTrack.id, source: 'autoplay' });
        }
      } else {
        audio.src = '';
      }
      return;
    }

    if (isPlaying && userInitiatedPlayRef.current) {
      audio.play().catch(handleAudioError);
    } else {
      audio.pause();
    }
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: audio element ref access intentional
  }, [currentTrack?.id, currentTrack?.file, isPlaying, activeIndex]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentTrack) {
      navigator.mediaSession.playbackState = 'none';
      return;
    }
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title,
      artist: currentTrack.artist,
      album: currentTrack.genre,
      artwork: [
        { src: currentTrack.cover, sizes: '512x512', type: 'image/jpeg' },
      ],
    });
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    navigator.mediaSession.setActionHandler('play', () => {
      // Guard : ignore OS-dispatched play actions on tab restore.
      // Only honor MediaSession 'play' once user has clicked play in-app this session.
      if (!userInitiatedPlayRef.current) return;
      setIsPlaying(true);
    });
    navigator.mediaSession.setActionHandler('pause', () => setIsPlaying(false));
    navigator.mediaSession.setActionHandler('previoustrack', playPrev);
    navigator.mediaSession.setActionHandler('nexttrack', playNext);
    navigator.mediaSession.setActionHandler('seekto', (e) => {
      const audio = activeAudioRef.current;
      if (audio && e.seekTime != null) audio.currentTime = e.seekTime;
    });
    navigator.mediaSession.setActionHandler('seekbackward', (e) => {
      const audio = activeAudioRef.current;
      if (!audio) return;
      audio.currentTime = Math.max(0, audio.currentTime - (e.seekOffset || 10));
    });
    navigator.mediaSession.setActionHandler('seekforward', (e) => {
      const audio = activeAudioRef.current;
      if (!audio) return;
      audio.currentTime = Math.min(
        audio.duration || 0,
        audio.currentTime + (e.seekOffset || 10)
      );
    });
  }, [currentTrack, playPrev, playNext, setIsPlaying, isPlaying, activeIndex]); // eslint-disable-line -- react-hooks/exhaustive-deps: mediaSession handlers intentional

  useEffect(() => {
    return () => {
      if ('mediaSession' in navigator) {
        [
          'play',
          'pause',
          'previoustrack',
          'nexttrack',
          'seekto',
          'seekbackward',
          'seekforward',
        ].forEach((action) =>
          navigator.mediaSession.setActionHandler(action, null)
        );
      }
    };
  }, []);

  useEffect(() => {
    const audioA = audioRefA.current;
    const audioB = audioRefB.current;
    if (audioA) audioA.volume = volume;
    if (audioB) audioB.volume = volume;
  }, [volume]);

  const handleTimeUpdate = useCallback(() => {
    const audio = activeAudioRef.current;
    if (audio) {
      const curr = audio.currentTime;
      const dur = audio.duration || 1;
      setCurrentTime(curr);
      setDuration(dur);
      setProgress((curr / dur) * 100);
      if (onTimeUpdate) onTimeUpdate(curr, dur);

      if (
        'mediaSession' in navigator &&
        'setPositionState' in navigator.mediaSession
      ) {
        try {
          navigator.mediaSession.setPositionState({
            duration: Number.isFinite(dur) ? dur : 0,
            playbackRate: audio.playbackRate || 1,
            position: Math.min(curr, Number.isFinite(dur) ? dur : curr),
          });
        } catch {
          // setPositionState throws if values are invalid — silent
        }
      }
    }
  }, [activeAudioRef, onTimeUpdate]);

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const audio = activeAudioRef.current;
    if (audio) audio.currentTime = pct * audio.duration;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!currentTrack) {
    return (
      <div className="player player-empty">
        <div className="player-inner">
          <div className="player-track-info">
            <span className="player-empty-text">
              Sélectionnez un morceau pour commencer
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="player" role="region" aria-label="Lecteur audio">
      {/* Primary audio element */}
      <audio
        ref={audioRefA}
        onTimeUpdate={activeIndex === 0 ? handleTimeUpdate : undefined}
        onEnded={activeIndex === 0 ? handleTrackEnd : undefined}
        onLoadedMetadata={activeIndex === 0 ? handleTimeUpdate : undefined}
        crossOrigin="anonymous"
        x-webkit-airplay="allow"
        preload="metadata"
      />
      {/* Secondary audio element (crossfade target) */}
      <audio
        ref={audioRefB}
        onTimeUpdate={activeIndex === 1 ? handleTimeUpdate : undefined}
        onEnded={activeIndex === 1 ? handleTrackEnd : undefined}
        onLoadedMetadata={activeIndex === 1 ? handleTimeUpdate : undefined}
        crossOrigin="anonymous"
        x-webkit-airplay="allow"
        preload="metadata"
      />
      <div className="player-inner">
        {/* Track info - left */}
        <div className="player-track-info">
          <PlaylistCover
            cover={currentTrack.cover}
            name={currentTrack.title}
            size={56}
            className="player-cover"
            onClick={onOpenFullscreen}
            style={onOpenFullscreen ? { cursor: 'pointer' } : undefined}
          />
          <div className="player-meta">
            <button
              type="button"
              className="player-title-btn"
              onClick={onOpenFullscreen}
              aria-label={`Ouvrir le plein écran — ${currentTrack.title}`}
            >
              {currentTrack.title}
            </button>
            <span className="player-artist">{currentTrack.artist}</span>
          </div>
          <button
            className={`player-like${isLiked(currentTrack.id) ? ' liked' : ''}`}
            onClick={() => toggleLike(currentTrack.id)}
            aria-label={
              isLiked(currentTrack.id)
                ? 'Retirer des favoris'
                : 'Ajouter aux favoris'
            }
          >
            <Heart
              size={20}
              fill={isLiked(currentTrack.id) ? 'currentColor' : 'none'}
              strokeWidth={isLiked(currentTrack.id) ? 0 : 1.8}
            />
          </button>

          {/* Add-to-playlist button */}
          <span
            className="player-add-cell"
            ref={showAddMenu ? addMenuRef : null}
          >
            <button
              type="button"
              className={`player-action-btn${showAddMenu ? ' active' : ''}`}
              onClick={() => setShowAddMenu((v) => !v)}
              aria-label="Ajouter à une playlist"
              aria-haspopup="menu"
              aria-expanded={showAddMenu}
              data-tooltip="Ajouter à une playlist"
            >
              <ListPlus size={20} />
            </button>
            {showAddMenu && (
              <div className="player-add-menu" role="menu">
                <button
                  type="button"
                  className="player-add-menu-item player-add-menu-queue"
                  onClick={() => {
                    if (!currentTrack) return;
                    addToQueue(currentTrack);
                    toasts.success?.('Ajoutée à la file');
                    setShowAddMenu(false);
                    capture('queue_added', {
                      trackId: currentTrack.id,
                      source: 'player',
                    });
                  }}
                >
                  <ListEnd size={16} aria-hidden="true" />
                  <span>Ajouter à la file d'attente</span>
                </button>
                <div className="player-add-menu-divider" aria-hidden="true" />
                <div className="player-add-menu-title">
                  Ajouter à une playlist…
                </div>
                {!creatingNew && userPlaylists.playlists.length === 0 && (
                  <div className="player-add-menu-empty">
                    Aucune playlist personnelle
                  </div>
                )}
                {!creatingNew &&
                  userPlaylists.playlists.map((up) => (
                    <button
                      key={up.id}
                      type="button"
                      className="player-add-menu-item"
                      onClick={() => addCurrentToPlaylist(up.id, up.name)}
                    >
                      {up.name}
                    </button>
                  ))}
                {creatingNew ? (
                  <div className="player-add-create-form">
                    <input
                      ref={newNameInputRef}
                      type="text"
                      className="player-add-create-input"
                      placeholder="Nom de la playlist"
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitCreatePlaylist();
                        if (e.key === 'Escape') cancelCreatePlaylist();
                      }}
                      maxLength={60}
                    />
                    <div className="player-add-create-actions">
                      <button
                        type="button"
                        className="player-add-create-cancel"
                        onClick={cancelCreatePlaylist}
                        aria-label="Annuler"
                      >
                        <X size={14} />
                      </button>
                      <button
                        type="button"
                        className="player-add-create-submit"
                        onClick={submitCreatePlaylist}
                        disabled={!newPlaylistName.trim()}
                        aria-label="Créer"
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="player-add-menu-create"
                    onClick={startCreatePlaylist}
                  >
                    <Plus size={14} /> Créer une nouvelle playlist
                  </button>
                )}
              </div>
            )}
          </span>
        </div>

        {/* Controls - center */}
        <div className="player-controls" data-tour="player-controls">
          <div className="player-buttons">
            <button
              className={`player-btn player-mode-btn is-${playMode}`}
              onClick={cyclePlayMode}
              aria-label={PLAY_MODES[playMode].label}
              data-tooltip={PLAY_MODES[playMode].label}
            >
              <ActiveModeIcon size={20} strokeWidth={2} />
            </button>
            <button
              className="player-btn player-seek-btn"
              onClick={() => seekRelative(-10)}
              aria-label="Reculer de 10 secondes"
              data-tooltip="Reculer 10s"
            >
              <SeekIcon direction="back" size={28} />
            </button>
            <button
              className="player-btn small"
              onClick={playPrev}
              aria-label="Piste précédente"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M3.3 1a.7.7 0 01.7.7v5.15l9.95-5.744a.7.7 0 011.05.606v12.575a.7.7 0 01-1.05.607L4 9.149V14.3a.7.7 0 01-.7.7H2.7a.7.7 0 01-.7-.7V1.7a.7.7 0 01.7-.7h.6z" />
              </svg>
            </button>
            <button
              className="player-btn play-btn"
              onClick={() => setIsPlaying(!isPlaying)}
              aria-label={
                isPlaying
                  ? `Mettre en pause : ${currentTrack.title}`
                  : `Lire : ${currentTrack.title}`
              }
            >
              {isPlaying ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M2.7 1a.7.7 0 00-.7.7v12.6a.7.7 0 00.7.7h2.6a.7.7 0 00.7-.7V1.7a.7.7 0 00-.7-.7H2.7zm8 0a.7.7 0 00-.7.7v12.6a.7.7 0 00.7.7h2.6a.7.7 0 00.7-.7V1.7a.7.7 0 00-.7-.7h-2.6z" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M3 1.713a.7.7 0 011.05-.607l10.89 6.288a.7.7 0 010 1.212L4.05 14.894A.7.7 0 013 14.288V1.713z" />
                </svg>
              )}
            </button>
            <button
              className="player-btn small"
              onClick={() => playNext('manual')}
              aria-label="Piste suivante"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M12.7 1a.7.7 0 00-.7.7v5.15L2.05 1.107A.7.7 0 001 1.713v12.575a.7.7 0 001.05.607L12 9.149V14.3a.7.7 0 00.7.7h.6a.7.7 0 00.7-.7V1.7a.7.7 0 00-.7-.7h-.6z" />
              </svg>
            </button>
            <button
              className="player-btn player-seek-btn"
              onClick={() => seekRelative(10)}
              aria-label="Avancer de 10 secondes"
              data-tooltip="Avancer 10s"
            >
              <SeekIcon direction="forward" size={28} />
            </button>
            <button
              className={`player-btn player-repeat-btn is-${repeat}`}
              onClick={cycleRepeatMode}
              aria-label={REPEAT_MODES[repeat].label}
              data-tooltip={REPEAT_MODES[repeat].label}
              aria-pressed={repeat !== 'off'}
            >
              <ActiveRepeatIcon size={20} strokeWidth={2} />
            </button>
          </div>
          <div className="player-progress-row">
            <span className="player-time">{formatTime(currentTime)}</span>
            <div className="player-progress-bar" onClick={handleSeek}>
              <div
                className="player-progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="player-time">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume + Queue - right */}
        <div className="player-volume">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M9.741.85a.75.75 0 01.375.65v13a.75.75 0 01-1.125.65l-6.925-4a3.642 3.642 0 01-1.33-4.967 3.639 3.639 0 011.33-1.332l6.925-4a.75.75 0 01.75 0z" />
            <path d="M11.5 4.75a.75.75 0 011.5 0v6.5a.75.75 0 01-1.5 0v-6.5z" />
            <path d="M14 3.5a.75.75 0 011.5 0v9a.75.75 0 01-1.5 0v-9z" />
          </svg>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="volume-slider"
            aria-label="Volume"
            aria-valuetext={`${Math.round(volume * 100)}%`}
          />
          {onOpenQueue && (
            <button
              className={`player-btn small${isQueueOpen ? ' active' : ''}`}
              onClick={onOpenQueue}
              data-tooltip={isQueueOpen ? 'Fermer la file' : "File d'attente"}
              aria-label={
                isQueueOpen
                  ? 'Fermer la file de lecture'
                  : 'Ouvrir la file de lecture'
              }
              aria-pressed={!!isQueueOpen}
            >
              <ListMusic size={18} />
            </button>
          )}
          {/* Phase 3 Step 5 — AirPlay 2 / audio output picker */}
          {audioOutputSupported && (
            <button
              type="button"
              className="player-btn small player-output-btn"
              onClick={pickOutput}
              disabled={audioOutputPicking}
              aria-label="Choisir la sortie audio"
              data-tooltip="AirPlay"
            >
              <Speaker size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Player;
